import {
  Activity,
  Children,
  type ComponentProps,
  createContext,
  isValidElement,
  type ReactElement,
  type ReactNode,
  useCallback,
  useMemo,
  useState
} from 'react';

import { useFlowlistHistory } from '@/hooks/useFlowlistHistory';
import { type Loading, useLoading } from '@/hooks/useLoading';

import { FlowList } from './FlowList';

export type FlowListName =
  | 'natural-person'
  | 'legal-entity'
  | 'legal-entity-partner-validated'
  | 'legal-entity-partner-invalidated'
  | 'legal-entity-representative'
  | 'error'
  | 'loading'
  | 'address-fidc'
  | 'document-not-found'
  | 'pending';

type FlowListElement = ReactElement<ComponentProps<typeof FlowList>, typeof FlowList>;

export type CurrentError = {
  lastFlow: FlowListName;
  tag?: string;
  label?: string;
  title?: string;
  subtitle?: ReactNode;
  helpDeskUrl?: string;
  documentValidation?: {
    action: () => Promise<void>;
  };
};

export const WizardContext = createContext<{
  currentFlowList?: FlowListName;
  setCurrentFlowList: (name: FlowListName) => void;
  visitedSteps: ReadonlySet<string>;
  markStepVisited: (name: string) => void;
  triggerError: (error: Omit<CurrentError, 'lastFlow'> & { lastFlow?: FlowListName }) => void;
  currentError?: CurrentError;
  tryAgain: () => void;
  triggerLoading: (loading: Omit<Loading, 'lastFlow'>) => void;
  loading?: Loading;
  clearLoading: () => void;
  flowlistHistory: ReadonlySet<FlowListName>;
  removeFromHistory: (name: FlowListName) => void;
  pendingFieldErrors?: Record<string, string>;
  clearPendingFieldErrors: () => void;
}>({
  setCurrentFlowList: (_: FlowListName) => {},
  visitedSteps: new Set<string>(),
  markStepVisited: (_: string) => {},
  triggerError: (_: Omit<CurrentError, 'lastFlow'>) => {},
  tryAgain: () => {},
  currentError: undefined,
  triggerLoading: (_: Omit<Loading, 'lastFlow'>) => {},
  loading: undefined,
  clearLoading: () => {},
  flowlistHistory: new Set<FlowListName>(),
  removeFromHistory: () => {},
  pendingFieldErrors: undefined,
  clearPendingFieldErrors: () => {}
});

const isFlowListElement = (child: ReactNode): child is FlowListElement => {
  return isValidElement(child) && child.type === FlowList;
};

type WizardProps = {
  children: FlowListElement | FlowListElement[];
  defaultSelected?: FlowListName;
};

export const Wizard = ({ children, defaultSelected }: WizardProps) => {
  const flowLists = Children.toArray(children)
    .filter(child => isFlowListElement(child))
    .map(child => {
      return { name: child.props.name as FlowListName, element: child };
    });
  const [currentFlowList, setCurrentFlowList] = useState<FlowListName>(defaultSelected ?? flowLists[0]?.name);
  const { flowlistHistory, removeFromHistory } = useFlowlistHistory(currentFlowList);
  const [visitedSteps, setVisitedSteps] = useState<ReadonlySet<string>>(() => new Set());

  const [currentError, setCurrentError] = useState<CurrentError | undefined>(undefined);
  const [pendingFieldErrors, setPendingFieldErrors] = useState<Record<string, string> | undefined>(undefined);

  const clearPendingFieldErrors = useCallback(() => {
    setPendingFieldErrors(undefined);
  }, []);

  const triggerError = useCallback(
    (error: Omit<CurrentError, 'lastFlow'> & { lastFlow?: FlowListName }) => {
      setCurrentFlowList(previous => {
        setCurrentError({ lastFlow: error.lastFlow ?? previous, ...error });
        return 'error';
      });
    },
    [setCurrentFlowList, setCurrentError]
  );

  const { loading, triggerLoading, clearLoading } = useLoading(setCurrentFlowList, triggerError, setPendingFieldErrors);

  const tryAgain = useCallback(() => {
    if (!currentError) {
      return;
    }

    setCurrentFlowList(currentError.lastFlow);
    setCurrentError(undefined);
  }, [currentError, setCurrentFlowList, setCurrentError]);

  // Idempotent: re-marking the same step name keeps the same Set reference so
  // consumers don't re-render needlessly.
  const markStepVisited = useCallback((name: string) => {
    setVisitedSteps(prev => {
      if (prev.has(name)) {
        return prev;
      }

      const next = new Set(prev);
      next.add(name);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({
      setCurrentFlowList,
      currentFlowList,
      visitedSteps,
      markStepVisited,
      triggerError,
      tryAgain,
      currentError,
      triggerLoading,
      clearLoading,
      loading,
      flowlistHistory,
      removeFromHistory,
      pendingFieldErrors,
      clearPendingFieldErrors
    }),
    [
      currentFlowList,
      visitedSteps,
      markStepVisited,
      triggerError,
      tryAgain,
      currentError,
      triggerLoading,
      clearLoading,
      loading,
      flowlistHistory,
      removeFromHistory,
      pendingFieldErrors,
      clearPendingFieldErrors
    ]
  );

  return (
    <WizardContext.Provider value={value}>
      {flowLists.map(flowList => (
        <Activity key={flowList.name} mode={currentFlowList === flowList.name ? 'visible' : 'hidden'}>
          {flowList.element}
        </Activity>
      ))}
    </WizardContext.Provider>
  );
};

Wizard.FlowList = FlowList;
